import React, { useEffect, useRef, useState } from 'react';

/**
 * A minimal line+area trend chart that always spans its container edge to
 * edge (via preserveAspectRatio="none"), so it reads as a complete chart
 * even when the underlying values are small or mostly zero - unlike a bar
 * chart, whose bars can end up as sparse, disconnected slivers at low
 * magnitudes. Hovering shows the nearest day's value in a small tooltip.
 */
export default function Sparkline({
    data,
    labels,
    colorClassName = 'text-orange-500',
    height = 80,
}: {
    data: number[];
    labels: string[];
    colorClassName?: string;
    height?: number;
}) {
    const [animate, setAnimate] = useState(false);
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const frame = requestAnimationFrame(() => setAnimate(true));
        return () => cancelAnimationFrame(frame);
    }, []);

    const max = Math.max(...data, 1);
    const points = data.map((value, i) => ({
        x: data.length > 1 ? (i / (data.length - 1)) * 100 : 50,
        y: 92 - (value / max) * 80,
    }));
    const linePoints = points.map((p) => `${p.x},${p.y}`).join(' ');
    const areaPoints = `0,100 ${linePoints} 100,100`;

    const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!containerRef.current || data.length === 0) return;
        const rect = containerRef.current.getBoundingClientRect();
        const relativeX = (event.clientX - rect.left) / rect.width;
        const index = Math.round(relativeX * (data.length - 1));
        setHoverIndex(Math.max(0, Math.min(data.length - 1, index)));
    };

    const hovered = hoverIndex !== null ? points[hoverIndex] : null;
    const dotTopPx = hovered ? (hovered.y / 100) * height : 0;

    let tooltipAlign = 'translate(-50%, -100%)';
    if (hovered) {
        if (hovered.x > 75) tooltipAlign = 'translate(-100%, -100%)';
        else if (hovered.x < 25) tooltipAlign = 'translate(0, -100%)';
    }

    return (
        <div
            ref={containerRef}
            className="relative w-full"
            style={{ height }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoverIndex(null)}
        >
            <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className={`w-full h-full ${colorClassName}`}
                style={{ opacity: animate ? 1 : 0, transition: 'opacity 500ms ease-out' }}
            >
                <polygon points={areaPoints} fill="currentColor" opacity="0.12" />
                <polyline
                    points={linePoints}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                />
            </svg>

            {hovered && hoverIndex !== null && (
                <>
                    <div
                        className={`absolute top-0 bottom-0 w-px ${colorClassName}`}
                        style={{
                            left: `${hovered.x}%`,
                            backgroundColor: 'currentColor',
                            opacity: 0.3,
                        }}
                    />
                    <div
                        className={`absolute w-2.5 h-2.5 rounded-full ${colorClassName}`}
                        style={{
                            left: `${hovered.x}%`,
                            top: dotTopPx,
                            backgroundColor: 'currentColor',
                            transform: 'translate(-50%, -50%)',
                            boxShadow: '0 0 0 2px var(--color-surface)',
                        }}
                    />
                    <div
                        className="absolute px-2 py-1 rounded-md bg-surface border border-border shadow-lg text-xs whitespace-nowrap pointer-events-none z-10"
                        style={{
                            left: `${hovered.x}%`,
                            top: Math.max(dotTopPx - 10, 0),
                            transform: tooltipAlign,
                        }}
                    >
                        <span className="text-foreground-secondary">{labels[hoverIndex]}:</span>{' '}
                        <span className="font-semibold text-foreground">{data[hoverIndex]}</span>
                    </div>
                </>
            )}
        </div>
    );
}
